import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Download, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking, paid, timeout, error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        if (attempts >= MAX_POLLS) {
          setState("error");
          return;
        }
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 font-sans">
      <div className="max-w-lg w-full bg-[#111315] border border-white/10 p-10 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#FF3B30] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-black uppercase tracking-tight mb-2">Confirming Payment</h1>
            <p className="text-[#8E8E93] text-sm font-mono">Verifying your subscription with the ledger…</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#30D158] mx-auto mb-6" />
            <h1 className="text-2xl font-black uppercase tracking-tight mb-2" data-testid="payment-success-title">Subscription Active</h1>
            <p className="text-[#8E8E93] text-sm leading-relaxed mb-6">
              Your license is confirmed. Download your included datasets below — each ZIP contains the dataset CSV
              plus its skill instructions and integration notes. Keep this page link to return to your downloads.
            </p>
            {info?.tier && (
              <div className="font-mono text-xs text-[#8E8E93] border border-white/10 bg-black/50 p-3 mb-6">
                PLAN: {info.tier.toUpperCase()} • TERM: {info.term === "2yr" ? "2-YEAR" : "1-YEAR"}
              </div>
            )}
            {access?.datasets?.length > 0 ? (
              <div className="text-left space-y-2 mb-6" data-testid="download-list">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E8E93] mb-2">Your downloads</div>
                {access.datasets.map((d) => (
                  <a
                    key={d.id}
                    href={`${API}/access/${info.access_token}/download/${d.id}`}
                    className="flex items-center justify-between gap-3 border border-white/10 hover:border-[#FF3B30] bg-black/40 px-4 py-3 text-sm transition-colors group"
                    data-testid={`download-${d.id}`}
                  >
                    <span className="flex items-center gap-2 text-white"><FileText className="w-4 h-4 text-[#8E8E93]" /> {d.name}</span>
                    <Download className="w-4 h-4 text-[#8E8E93] group-hover:text-[#FF3B30]" />
                  </a>
                ))}
              </div>
            ) : (
              <div className="font-mono text-xs text-[#8E8E93] mb-6 flex items-center justify-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading your downloads…
              </div>
            )}
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto mb-6" />
            <h1 className="text-2xl font-black uppercase tracking-tight mb-2">Still Processing</h1>
            <p className="text-[#8E8E93] text-sm leading-relaxed mb-6">
              We couldn't confirm the payment status just yet. If you completed checkout, you'll receive a
              confirmation email shortly. Otherwise, please try again.
            </p>
          </>
        )}
        <Link
          to="/"
          className="inline-flex items-center gap-2 border border-white/20 hover:border-white hover:bg-white hover:text-black px-6 py-3 text-xs font-mono uppercase tracking-wider transition-colors"
          data-testid="payment-success-home-link"
        >
          <ArrowLeft className="w-4 h-4" /> Return to Premi-Skills
        </Link>
      </div>
    </div>
  );
}
